import React, { useLayoutEffect, useRef, useState } from "react";
import {
  MoreHorizontal,
  Pencil,
  Plus,
  Settings,
  Trash2,
  Users,
} from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";

function formatRelativeTime(iso) {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    const now = new Date();
    const diff = (now - d) / 1000;
    if (diff < 60) return "just now";
    if (diff < 3600) return `${Math.floor(diff / 60)}m`;
    if (diff < 86400) return `${Math.floor(diff / 3600)}h`;
    if (diff < 86400 * 7) return `${Math.floor(diff / 86400)}d`;
    return d.toLocaleDateString(undefined, { month: "short", day: "numeric" });
  } catch {
    return "";
  }
}

const ROLE_LABEL = { owner: "Owner", admin: "Admin", member: "Member" };

export default function Sidebar({
  workspace,
  chats,
  loading,
  activeChatId,
  onSelectChat,
  onNewChat,
  onRenameChat,
  onDeleteChat,
  onOpenSettings,
}) {
  const [renameOpen, setRenameOpen] = useState(false);
  const [renameValue, setRenameValue] = useState("");
  const [renameTargetId, setRenameTargetId] = useState(null);
  const [deleteTargetId, setDeleteTargetId] = useState(null);

  // UX (chat-sort-flip, Feb 2026): server now returns chats OLDEST→NEWEST so
  // the newest is at the bottom of the rendered list. We auto-scroll the
  // scroll container to the bottom on mount + whenever the list refreshes,
  // BUT only if the user is already near the bottom (within 100px). That
  // way we never yank a user who is reading older threads back down to the
  // present.
  const scrollRef = useRef(null);
  const stuckToBottomRef = useRef(true); // bottom-locked by default
  const firstPaintRef = useRef(true);
  const lastChatCountRef = useRef(0);

  const NEAR_BOTTOM_PX = 100;

  // Track "is the user near the bottom right now?" — updated on every scroll.
  const onScroll = () => {
    const el = scrollRef.current;
    if (!el) return;
    const distFromBottom = el.scrollHeight - el.scrollTop - el.clientHeight;
    stuckToBottomRef.current = distFromBottom <= NEAR_BOTTOM_PX;
  };

  // useLayoutEffect so we scroll BEFORE the browser paints — no flash of
  // the list being at the top on initial mount with many chats.
  useLayoutEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    if (loading) return; // wait until the list has actually rendered
    const newCount = chats.length;
    const prevCount = lastChatCountRef.current;
    lastChatCountRef.current = newCount;

    // First paint with content present → always scroll to bottom.
    if (firstPaintRef.current && newCount > 0) {
      el.scrollTop = el.scrollHeight;
      firstPaintRef.current = false;
      stuckToBottomRef.current = true;
      return;
    }

    // Subsequent refreshes — only follow the bottom if the user was
    // already there OR if a brand-new chat just appeared and the user
    // is the one who added it (count grew while we were near bottom).
    if (stuckToBottomRef.current && newCount >= prevCount) {
      el.scrollTop = el.scrollHeight;
    }
  }, [chats, loading]);

  const openRename = (chat) => {
    setRenameTargetId(chat.id);
    setRenameValue(chat.title || "");
    setRenameOpen(true);
  };

  const submitRename = (e) => {
    e?.preventDefault?.();
    if (renameTargetId && renameValue.trim()) {
      onRenameChat(renameTargetId, renameValue.trim());
    }
    setRenameOpen(false);
    setRenameTargetId(null);
    setRenameValue("");
  };

  const memberCount = workspace?.member_count;

  return (
    <aside
      className="w-80 flex flex-col border-r border-[#DCD7CE] bg-[#F3F1EC] h-full flex-shrink-0"
      data-testid="sidebar"
    >
      {/* Workspace header */}
      <div className="px-5 pt-6 pb-4 border-b border-[#DCD7CE]/70">
        <div className="flex items-start justify-between gap-2 mb-1">
          <div className="min-w-0">
            <div
              className="font-serif text-2xl tracking-tight text-[#2C2A29] leading-tight truncate"
              data-testid="sidebar-workspace-name"
            >
              {workspace?.name || "Workspace"}
              <span className="text-[#1E7BFF]">.</span>
            </div>
            <div className="flex items-center gap-2 mt-1">
              <span className="font-sans text-[10px] uppercase tracking-[0.22em] text-[#7A7571] font-semibold">
                {ROLE_LABEL[workspace?.role] || "Member"}
              </span>
              {typeof memberCount === "number" ? (
                <span
                  className="flex items-center gap-1 font-sans text-[11px] text-[#7A7571]"
                  data-testid="sidebar-member-count"
                >
                  <Users size={11} strokeWidth={1.8} />
                  {memberCount}
                </span>
              ) : null}
            </div>
          </div>
          <button
            type="button"
            onClick={onOpenSettings}
            data-testid="sidebar-settings-button"
            title="Workspace settings"
            aria-label="Workspace settings"
            className="p-2 rounded-lg text-[#7A7571] hover:text-[#2C2A29] hover:bg-[#E8E5DF] transition-colors"
          >
            <Settings size={16} strokeWidth={1.8} />
          </button>
        </div>
      </div>

      {/* New chat */}
      <div className="px-5 pt-5 pb-4">
        <button
          onClick={onNewChat}
          data-testid="new-chat-button"
          className="w-full flex items-center justify-between px-4 py-3 rounded-xl bg-[#2C2A29] text-white hover:bg-[#1A1918] transition-colors group"
        >
          <span className="font-sans text-sm font-medium tracking-wide">
            New chat
          </span>
          <Plus
            size={16}
            strokeWidth={2}
            className="opacity-90 group-hover:rotate-90 transition-transform duration-200"
          />
        </button>
      </div>

      <div className="px-5 mb-2">
        <p className="font-sans text-[10px] uppercase tracking-[0.22em] text-[#7A7571] font-semibold">
          Conversations
        </p>
      </div>

      <div
        ref={scrollRef}
        onScroll={onScroll}
        className="flex-1 overflow-y-auto pb-4"
        data-testid="chat-list-scroll"
      >
        {loading ? (
          <div className="px-5 py-8 font-sans text-sm text-[#7A7571]">
            Loading conversations…
          </div>
        ) : chats.length === 0 ? (
          <div className="px-5 py-12 text-center" data-testid="chat-list-empty">
            <p className="font-sans text-sm text-[#7A7571] leading-relaxed">
              No conversations yet.
              <br />
              Start one above.
            </p>
          </div>
        ) : (
          <ul className="space-y-1 px-3">
            {chats.map((chat) => {
              const isActive = chat.id === activeChatId;
              return (
                <li key={chat.id} data-testid={`chat-list-item-${chat.id}`}>
                  <div
                    className={`group relative px-4 py-3 rounded-xl cursor-pointer transition-all flex flex-col gap-1 border ${
                      isActive
                        ? "bg-white shadow-sm border-[#DCD7CE]"
                        : "border-transparent hover:bg-[#E8E5DF]"
                    }`}
                    onClick={() => onSelectChat(chat.id)}
                    data-testid={`chat-list-item-button-${chat.id}`}
                  >
                    <div className="flex items-start justify-between gap-2">
                      <span className="font-serif text-lg truncate text-[#2C2A29] leading-tight flex-1">
                        {chat.title || "Untitled"}
                      </span>
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <button
                            onClick={(e) => e.stopPropagation()}
                            data-testid={`chat-menu-button-${chat.id}`}
                            className={`p-1 rounded-md text-[#7A7571] hover:bg-[#DCD7CE]/70 transition-colors ${
                              isActive
                                ? "opacity-100"
                                : "opacity-0 group-hover:opacity-100"
                            }`}
                            aria-label="Chat options"
                          >
                            <MoreHorizontal size={16} />
                          </button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent
                          align="end"
                          onClick={(e) => e.stopPropagation()}
                          data-testid={`chat-menu-${chat.id}`}
                        >
                          <DropdownMenuItem
                            onClick={() => openRename(chat)}
                            data-testid={`chat-menu-rename-${chat.id}`}
                          >
                            <Pencil size={14} className="mr-2" />
                            Rename
                          </DropdownMenuItem>
                          <DropdownMenuSeparator />
                          <DropdownMenuItem
                            onClick={() => setDeleteTargetId(chat.id)}
                            className="text-[#8C3A1F] focus:text-[#8C3A1F]"
                            data-testid={`chat-menu-delete-${chat.id}`}
                          >
                            <Trash2 size={14} className="mr-2" />
                            Delete
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </div>
                    <div className="flex items-center gap-2 min-w-0">
                      <p className="font-sans text-sm text-[#7A7571] truncate flex-1">
                        {chat.last_message_preview ||
                          `Started by ${chat.created_by_name || "—"}`}
                      </p>
                      <span className="font-sans text-[10px] text-[#A5A29E] shrink-0">
                        {formatRelativeTime(chat.updated_at)}
                      </span>
                    </div>
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </div>

      {/* Rename dialog */}
      <AlertDialog open={renameOpen} onOpenChange={setRenameOpen}>
        <AlertDialogContent data-testid="rename-chat-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-serif text-2xl">
              Rename conversation
            </AlertDialogTitle>
            <AlertDialogDescription>
              Give this conversation a clearer title.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <form onSubmit={submitRename} className="px-1">
            <input
              autoFocus
              value={renameValue}
              onChange={(e) => setRenameValue(e.target.value)}
              data-testid="rename-chat-input"
              className="w-full px-4 py-3 rounded-lg bg-white border border-[#DCD7CE] font-sans text-base text-[#2C2A29] focus:outline-none focus:ring-2 focus:ring-[#D96C4A]/30"
              placeholder="Conversation title"
            />
          </form>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="rename-chat-cancel">
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={submitRename}
              data-testid="rename-chat-save"
            >
              Save
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Delete confirmation */}
      <AlertDialog
        open={!!deleteTargetId}
        onOpenChange={(open) => !open && setDeleteTargetId(null)}
      >
        <AlertDialogContent data-testid="delete-chat-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-serif text-2xl">
              Delete this conversation?
            </AlertDialogTitle>
            <AlertDialogDescription>
              This removes the chat and all its messages for everyone in this
              workspace. There is no undo.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="delete-chat-cancel">
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              data-testid="delete-chat-confirm"
              onClick={() => {
                if (deleteTargetId) onDeleteChat(deleteTargetId);
                setDeleteTargetId(null);
              }}
              className="bg-[#8C3A1F] hover:bg-[#7A3019]"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </aside>
  );
}
